import type {
  CompanyBusinessType,
} from "@/types/company";

export type CommerceMode = "order" | "lead";

export type BusinessConfig = {
  businessType: CompanyBusinessType;

  itemSingular: string;
  itemPlural: string;

  itemSingularLower: string;
  itemPluralLower: string;

  newItemLabel: string;
  createItemLabel: string;
  editItemLabel: string;

  publicCatalogLabel: string;
  publicStoreLabel: string;

  publicPresenceLabel: string;
  publicPresenceLabelLower: string;

  primaryCta: string;

  publicItemPath:
    | "produto"
    | "imovel"
    | "veiculo"
    | "servico"
    | "cardapio"
    | "oferta"
    | "item";

  commerceMode: CommerceMode;

  supportsVariations: boolean;
  supportsStock: boolean;
  supportsShippingDimensions: boolean;
  supportsSku: boolean;
  supportsCommercialAttributes: boolean;

  // Linguagem e comportamento da área de Métricas
  metricsItemLabel: string;
  metricsRelationshipLabel: string;
  metricsConversionLabel: string;
  metricsPrimaryActionLabel: string;
};

const BUSINESS_CONFIG: Record<
  CompanyBusinessType,
  BusinessConfig
> = {
  retail: {
    businessType: "retail",
    supportsCommercialAttributes: true,
    itemSingular: "Produto",
    itemPlural: "Produtos",
    itemSingularLower: "produto",
    itemPluralLower: "produtos",
    newItemLabel: "Novo produto",
    createItemLabel: "Criar produto",
    editItemLabel: "Editar produto",
    publicCatalogLabel: "Produtos",
    publicStoreLabel: "Ver minha loja",
    publicPresenceLabel: "Loja",
    publicPresenceLabelLower: "loja",
    primaryCta: "Adicionar ao pedido",
    publicItemPath: "produto",
    commerceMode: "order",
    supportsVariations: true,
    supportsStock: true,
    supportsShippingDimensions: true,
    supportsSku: true,
    metricsItemLabel: "Produtos",
    metricsRelationshipLabel: "Clientes",
    metricsConversionLabel: "Conversão em pedido",
    metricsPrimaryActionLabel: "Pedidos",
  },

  wholesale: {
    businessType: "wholesale",
    supportsCommercialAttributes: true,
    itemSingular: "Produto",
    itemPlural: "Produtos",
    itemSingularLower: "produto",
    itemPluralLower: "produtos",
    newItemLabel: "Novo produto",
    createItemLabel: "Criar produto",
    editItemLabel: "Editar produto",
    publicCatalogLabel: "Catálogo de produtos",
    publicStoreLabel: "Ver minha vitrine",
    publicPresenceLabel: "Vitrine",
    publicPresenceLabelLower: "vitrine",
    primaryCta: "Adicionar ao pedido",
    publicItemPath: "produto",
    commerceMode: "order",
    supportsVariations: true,
    supportsStock: true,
    supportsShippingDimensions: true,
    supportsSku: true,
    metricsItemLabel: "Produtos",
    metricsRelationshipLabel: "Clientes",
    metricsConversionLabel: "Conversão em pedido",
    metricsPrimaryActionLabel: "Pedidos",
  },

  real_estate: {
    businessType: "real_estate",
    supportsCommercialAttributes: false,
    itemSingular: "Imóvel",
    itemPlural: "Imóveis",
    itemSingularLower: "imóvel",
    itemPluralLower: "imóveis",
    newItemLabel: "Novo imóvel",
    createItemLabel: "Cadastrar imóvel",
    editItemLabel: "Editar imóvel",
    publicCatalogLabel: "Imóveis",
    publicStoreLabel: "Ver minha vitrine",
    publicPresenceLabel: "Vitrine",
    publicPresenceLabelLower: "vitrine",
    primaryCta: "Tenho interesse",
    publicItemPath: "imovel",
    commerceMode: "lead",
    supportsVariations: false,
    supportsStock: false,
    supportsShippingDimensions: false,
    supportsSku: false,
    metricsItemLabel: "Imóveis",
    metricsRelationshipLabel: "Leads",
    metricsConversionLabel: "Conversão em lead",
    metricsPrimaryActionLabel: "Leads",
  },

  vehicles: {
    businessType: "vehicles",
    supportsCommercialAttributes: false,
    itemSingular: "Veículo",
    itemPlural: "Veículos",
    itemSingularLower: "veículo",
    itemPluralLower: "veículos",
    newItemLabel: "Novo veículo",
    createItemLabel: "Cadastrar veículo",
    editItemLabel: "Editar veículo",
    publicCatalogLabel: "Veículos",
    publicStoreLabel: "Ver minha vitrine",
    publicPresenceLabel: "Vitrine",
    publicPresenceLabelLower: "vitrine",
    primaryCta: "Tenho interesse",
    publicItemPath: "veiculo",
    commerceMode: "lead",
    supportsVariations: false,
    supportsStock: false,
    supportsShippingDimensions: false,
    supportsSku: true,
    metricsItemLabel: "Veículos",
    metricsRelationshipLabel: "Leads",
    metricsConversionLabel: "Conversão em lead",
    metricsPrimaryActionLabel: "Leads",
  },

  services: {
    businessType: "services",
    supportsCommercialAttributes: false,
    itemSingular: "Serviço",
    itemPlural: "Serviços",
    itemSingularLower: "serviço",
    itemPluralLower: "serviços",
    newItemLabel: "Novo serviço",
    createItemLabel: "Cadastrar serviço",
    editItemLabel: "Editar serviço",
    publicCatalogLabel: "Serviços",
    publicStoreLabel: "Ver minha vitrine",
    publicPresenceLabel: "Página",
    publicPresenceLabelLower: "página",
    primaryCta: "Solicitar orçamento",
    publicItemPath: "servico",
    commerceMode: "lead",
    supportsVariations: false,
    supportsStock: false,
    supportsShippingDimensions: false,
    supportsSku: false,
    metricsItemLabel: "Serviços",
    metricsRelationshipLabel: "Leads",
    metricsConversionLabel: "Conversão em solicitação",
    metricsPrimaryActionLabel: "Solicitações",
  },

  food: {
    businessType: "food",
    supportsCommercialAttributes: false,
    itemSingular: "Item",
    itemPlural: "Itens",
    itemSingularLower: "item",
    itemPluralLower: "itens",
    newItemLabel: "Novo item",
    createItemLabel: "Cadastrar item",
    editItemLabel: "Editar item",
    publicCatalogLabel: "Cardápio",
    publicStoreLabel: "Ver minha loja",
    publicPresenceLabel: "Loja",
    publicPresenceLabelLower: "loja",
    primaryCta: "Adicionar ao pedido",
    publicItemPath: "cardapio",
    commerceMode: "order",
    supportsVariations: true,
    supportsStock: false,
    supportsShippingDimensions: false,
    supportsSku: false,
    metricsItemLabel: "Itens",
    metricsRelationshipLabel: "Clientes",
    metricsConversionLabel: "Conversão em pedido",
    metricsPrimaryActionLabel: "Pedidos",
  },

  consortium_insurance: {
    businessType: "consortium_insurance",
    supportsCommercialAttributes: false,
    itemSingular: "Oferta",
    itemPlural: "Ofertas",
    itemSingularLower: "oferta",
    itemPluralLower: "ofertas",
    newItemLabel: "Nova oferta",
    createItemLabel: "Cadastrar oferta",
    editItemLabel: "Editar oferta",
    publicCatalogLabel: "Ofertas",
    publicStoreLabel: "Ver minha vitrine",
    publicPresenceLabel: "Vitrine",
    publicPresenceLabelLower: "vitrine",
    primaryCta: "Tenho interesse",
    publicItemPath: "oferta",
    commerceMode: "lead",
    supportsVariations: false,
    supportsStock: false,
    supportsShippingDimensions: false,
    supportsSku: false,
    metricsItemLabel: "Ofertas",
    metricsRelationshipLabel: "Leads",
    metricsConversionLabel: "Conversão em lead",
    metricsPrimaryActionLabel: "Leads",
  },

  other: {
    businessType: "other",
    supportsCommercialAttributes: false,
    itemSingular: "Item",
    itemPlural: "Itens",
    itemSingularLower: "item",
    itemPluralLower: "itens",
    newItemLabel: "Novo item",
    createItemLabel: "Cadastrar item",
    editItemLabel: "Editar item",
    publicCatalogLabel: "Vitrine",
    publicStoreLabel: "Ver minha vitrine",
    publicPresenceLabel: "Vitrine",
    publicPresenceLabelLower: "vitrine",
    primaryCta: "Tenho interesse",
    publicItemPath: "item",
    commerceMode: "lead",
    supportsVariations: false,
    supportsStock: false,
    supportsShippingDimensions: false,
    supportsSku: false,
    metricsItemLabel: "Itens",
    metricsRelationshipLabel: "Contatos",
    metricsConversionLabel: "Conversão em contato",
    metricsPrimaryActionLabel: "Contatos",
  },
};

export function getBusinessConfig(
  businessType?: CompanyBusinessType | null,
): BusinessConfig {
  if (!businessType) {
    return BUSINESS_CONFIG.retail;
  }

  return (
    BUSINESS_CONFIG[businessType] ??
    BUSINESS_CONFIG.retail
  );
}
